import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RestrictionSchemeType } from '@/data/models/territory';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import ContextErrorAlert from '../../common/ErrorAlert/ContextErrorAlert';
import DismissibleErrorAlert from '../../common/ErrorAlert/DismissibleErrorAlert';
import {
  merchandiseReleasesGridUrl,
  merchandiseSubmissionsGridUrl,
} from '../../common/Grid/GridUtilities';
import {
  getActiveChangeRequestUrl,
  getAlbumStateBadgeClass,
} from '../../common/ProductUtilities';
import LabelInfoModal from '../LabelInfoModal/LabelInfoModal';
import RestrictionsModal from '../Restrictions/RestrictionsModal';
import ActionDropdownMenu from '../ProductView/ActionDropdownMenu/ActionDropdownMenu';
import ProductMetadata from '../ProductView/ProductMetadata/ProductMetadata';
import '../ProductView/ProductView.scss';
import RejectionBanner from '../ProductView/RejectionBanner/RejectionBanner';
import StateChangeOperations from '../ProductView/StateChangeOperations/StateChangeOperations';
import ValidationErrors from '../ProductView/ValidationErrors/ValidationErrors';
import MerchProductTabs from '../ProductTabs/MerchProductTabs';
import MerchProductMetadataTable from '@/components/partials/MerchProductView/MerchProductMetadata/MerchProductMetadataTable';
import MerchProductDetailsEdit from '../ProductDetailsEdit/MerchProductDetailsEdit';
import { FormatType } from '@/data/models/formatType';
import { AlbumState } from '@/data/models/albumState';
import { ProductViewModes } from '@/utils/productStateUtils';
import PricingModal from '../MainContainer/Pricing/PricingModal';
import Spinner, { SpinnerSize } from '@/components/common/Spinner/Spinner';
import { ProductViewHook } from '@/types/product-view-model';
import { translatePhysicalProductType } from '@/helpers/translateProductType';
import ErrorAlert from '../../common/ErrorAlert/ErrorAlert';

interface MerchProductViewTemplateProps {
  productViewMode: ProductViewModes;
  viewHook: ProductViewHook;
}

export default function MerchProductViewTemplate({
  productViewMode,
  viewHook,
}: MerchProductViewTemplateProps) {
  const { t } = useTranslation();
  const {
    album,
    labelInfo,
    parentLabelInfo,
    restrictions,
    isLoading,
    errorFlag,
    errorBanner,
    errors,
    errorMessages,
    warningMessages,
    currentStateHistory,
    readonly,
    showRemoveFromProjectConfirmationModal,
    setShowRemoveFromProjectConfirmationModal,
    project,
    showEditProductModal,
    showRestrictionsModal,
    showLabelInfoModal,
    showParentLabelInfoModal,
    removeProject,
    toggleEditProductModal,
    toggleRestrictionsModal,
    toggleLabelInfoModal,
    toggleParentLabelInfoModal,
    updateProductRestrictions,
    onProductUpdated,
    showValidationErrors,
    setErrorBanner,
    setErrors,
    refetchProject,
    showPricingModal,
    togglePricingModal,
    updateProductPricing,
    changeRequest,
    currentChanges,
  } = viewHook;

  const [apiError, setApiError] = useState<string | null>(null);

  const { DetailsTable, EditMode, Tabs } = useMemo(() => {
    return {
      DetailsTable: MerchProductMetadataTable,
      EditMode: MerchProductDetailsEdit,
      Tabs: MerchProductTabs,
    };
  }, [album]);

  // Helper to determine if we should show validation errors in the modal
  const shouldShowValidationErrors = showValidationErrors();

  return (
    <div className="app-body">
      <ConfirmationModal
        header={t('product.productEditView.confirmRemoveFromProjectHeader')}
        message={t('product.productEditView.confirmRemoveFromProjectMessage', {
          projectName: project?.name,
        })}
        handleConfirm={removeProject}
        showModal={showRemoveFromProjectConfirmationModal}
        setShowModal={setShowRemoveFromProjectConfirmationModal}
      />
      <div className="editProductView">
        {album && !isLoading ? (
          <>
            <div>
              <div className="view-title-container">
                <div className="editProductView-header">
                  <span>
                    {album.albumState === AlbumState.Released ? (
                      <Link to={merchandiseReleasesGridUrl}>
                        {t('product.productList.merchandiseReleases')}
                      </Link>
                    ) : (
                      <Link to={merchandiseSubmissionsGridUrl}>
                        {t('product.productList.merchandiseSubmissions')}
                      </Link>
                    )}
                  </span>
                  <span>&gt;</span>
                  <span>{album.title}</span>
                </div>
                <div className="editProductView-badges">
                  <div className="badge format-badge badge-default">
                    {translatePhysicalProductType(album?.format?.name ?? null)}
                  </div>
                  {productViewMode !== ProductViewModes.ChangeRequest &&
                  productViewMode !== ProductViewModes.ChangeRequestReadOnly ? (
                    <div
                      className={`badge state-badge ${getAlbumStateBadgeClass(album?.albumState)}`}
                    >
                      {t(`product.${album?.albumState}`)}
                    </div>
                  ) : (
                    <>
                      <div className={`badge state-badge badge-alert`}>
                        {t(`product.changeRequest`)}
                      </div>
                      {changeRequest?.state ? (
                        <div
                          className={`badge state-badge ${getAlbumStateBadgeClass(changeRequest.state)}`}
                        >
                          {t(`product.${changeRequest.state}`)}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
                {productViewMode !== ProductViewModes.ChangeRequestReadOnly && (
                  <div className="editProductView-stateChangeOperations">
                    <StateChangeOperations
                      album={album}
                      setErrorBanner={setErrorBanner}
                      setErrors={setErrors}
                      setApiError={setApiError}
                      submissionsGridUrl={
                        productViewMode === ProductViewModes.ChangeRequest
                          ? merchandiseReleasesGridUrl
                          : merchandiseSubmissionsGridUrl
                      }
                      formatType={FormatType.Merchandise}
                      changeRequest={changeRequest}
                      hasChangeRequest={!!currentChanges?.totalChangesCounter}
                    ></StateChangeOperations>
                  </div>
                )}
                {productViewMode !== ProductViewModes.ChangeRequestReadOnly && (
                  <div className="editProductView-actiondDropdownMenu">
                    <ActionDropdownMenu
                      album={album}
                      setErrorBanner={setErrorBanner}
                      setErrors={setErrors}
                      project={project ?? undefined}
                      onProjectAdded={refetchProject}
                      onProjectRemoved={() =>
                        setShowRemoveFromProjectConfirmationModal(true)
                      }
                      formatType={FormatType.Merchandise}
                      productViewMode={productViewMode}
                      changeRequest={changeRequest}
                      hasChangeRequest={!!currentChanges?.totalChangesCounter}
                      readonly={readonly}
                    ></ActionDropdownMenu>
                  </div>
                )}
              </div>
              <div className="view-spacer-container"></div>

              <div className="editProductView-enableScroll view-content-container">
                <ContextErrorAlert />
                <DismissibleErrorAlert
                  error={apiError}
                  onClose={() => setApiError(null)}
                />
                {errorBanner ? (
                  <ValidationErrors
                    errorBanner={errorBanner}
                    errors={errorMessages}
                    warningMessages={warningMessages}
                    validationErrors={errors}
                    catalogUpc={album.gtin}
                    region={'View'}
                  ></ValidationErrors>
                ) : null}
                {album.activeChangeRequestId ? (
                  <ErrorAlert
                    titleText={t('product.changeRequest.pendingChangeRequest')}
                    dismissable={false}
                    variant={'warning'}
                    contentText={
                      <span>
                        {t('product.changeRequest.pendingChangeRequestInfo')}
                        <Link to={getActiveChangeRequestUrl(album?.id, true)}>
                          {` ${t('common.generic.clickHere')}`}
                        </Link>
                      </span>
                    }
                  />
                ) : null}
                <div className="editProductView-validationErrorBanner">
                  <RejectionBanner
                    albumState={currentStateHistory}
                  ></RejectionBanner>
                  <ProductMetadata
                    errors={errors}
                    errorFlag={errorFlag}
                    album={album}
                    project={project ?? undefined}
                    onToggleLabelInfoModal={toggleLabelInfoModal}
                    onToggleParentLabelInfoModal={toggleParentLabelInfoModal}
                    readonly={readonly}
                    onEditProductClicked={toggleEditProductModal}
                    DetailsTableComponent={DetailsTable}
                    changeRequest={changeRequest}
                    currentChanges={currentChanges?.productMetadataChanges}
                  />
                </div>
                <div className="editProductView-spacer-container"></div>
                <Tabs
                  isValidFormat={!!album.format}
                  restrictions={restrictions}
                  toggleRestrictionsModal={toggleRestrictionsModal}
                  togglePricingModal={togglePricingModal}
                  album={album}
                  readonly={readonly}
                  errors={errors}
                  stateHistory={album.albumMetaData?.stateHistory}
                  productViewMode={productViewMode}
                  currentChanges={currentChanges}
                  changeRequest={changeRequest}
                ></Tabs>
              </div>
            </div>
            <div>
              {showEditProductModal ? (
                <EditMode
                  togglePopUp={toggleEditProductModal}
                  albumObj={album}
                  errorFlag={errorFlag}
                  errors={errors}
                  readonly={readonly}
                  onProductUpdated={onProductUpdated}
                  showValidationErrors={shouldShowValidationErrors}
                  changeRequest={changeRequest}
                  currentChanges={currentChanges?.productMetadataChanges}
                ></EditMode>
              ) : null}
              {showLabelInfoModal && labelInfo ? (
                <LabelInfoModal
                  togglePopUp={toggleLabelInfoModal}
                  label={labelInfo}
                ></LabelInfoModal>
              ) : null}
              {showParentLabelInfoModal && parentLabelInfo ? (
                <LabelInfoModal
                  togglePopUp={toggleParentLabelInfoModal}
                  label={parentLabelInfo}
                  isParentLabel
                ></LabelInfoModal>
              ) : null}
              {showRestrictionsModal && restrictions ? (
                <RestrictionsModal
                  toggleRestrictionsModal={toggleRestrictionsModal}
                  territoryRestrictionSet={restrictions}
                  readonly={readonly}
                  updateProduct={(
                    restrictions: string[],
                    restrictionScheme: RestrictionSchemeType
                  ) =>
                    updateProductRestrictions(restrictions, restrictionScheme)
                  }
                  changeRequest={changeRequest}
                  currentChanges={currentChanges?.territoriesChanges}
                ></RestrictionsModal>
              ) : null}
              {showPricingModal ? (
                <PricingModal
                  togglePricingModal={togglePricingModal}
                  album={album}
                  readonly={readonly}
                  updateProductPricing={updateProductPricing}
                  changeRequest={changeRequest}
                  currentChanges={currentChanges?.pricingChanges}
                ></PricingModal>
              ) : null}
            </div>
          </>
        ) : (
          <div className={'spinner-container'}>
            <Spinner size={SpinnerSize.FullPage} />
          </div>
        )}
      </div>
    </div>
  );
}
